import { requireOptionalNativeModule } from 'expo-modules-core'

/**
 * ASO-RATING-2026-001 — the JS face of the App Store review native module.
 *
 * ⚠️ `requireOptionalNativeModule`, NOT `requireNativeModule` — the same rule, for the same reason,
 * as `modules/atlas-adservices/index.ts`. This is native code that exists only in a binary built
 * after it lands, while the JS below ships by OTA to every installed binary on runtime 5.0. The
 * throwing variant would crash those; the optional one returns `null`, which the caller
 * (`utils/appReviewPrompt.js`) reads as `unavailable` and does nothing.
 * `utils/__tests__/appStoreReviewNativeConfig.test.js` guards it structurally.
 *
 * No `isAvailable` on the native side, no "has the dialog been shown" query, no result: StoreKit
 * reports none of that, and the module does not pretend otherwise.
 */
interface AtlasStoreReviewNativeModule {
  requestReviewAsync(): Promise<void>
}

const nativeModule = requireOptionalNativeModule<AtlasStoreReviewNativeModule>('AtlasStoreReview')

/** Whether this binary can request the App Store review dialog at all. */
export function isAvailable(): boolean {
  return nativeModule != null
}

/**
 * Ask StoreKit to consider showing the review dialog.
 *
 * Resolves once the request has been handed to StoreKit — which says nothing about whether Apple
 * displayed it. REJECTS when there is no foreground-active window scene to present in, and when
 * the module is absent (callers are expected to check `isAvailable()` first; reaching this without
 * the module is a caller bug, and a silent resolve would record a request that was never made).
 */
export async function requestReviewAsync(): Promise<void> {
  if (!nativeModule) throw new Error('AtlasStoreReview native module is not present in this binary')
  await nativeModule.requestReviewAsync()
}

export default { isAvailable, requestReviewAsync }
